/**
 * Internal scheduling display preference.
 *
 * This never changes scheduler semantics: appointment instants are UTC and
 * business-hour civil values are interpreted in the tenant timezone. It only
 * chooses how authenticated users render those UTC instants in the UI.
 * Customer-facing output uses the service-location timezone separately.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  formatDateTimeInTimeZone,
  getViewerTimeZone,
  safeTimeZone,
  timeZoneAbbrev,
} from '@/lib/date-values';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { useMyProfile, useUpdateMyScheduleDisplayTimezone } from '@/hooks/useMyProfile';

/**
 * How an operator reads schedule instants. Storage is always UTC; this only
 * chooses the zone the UI renders those instants in.
 *
 * `service` is the odd one out: `tenant` and `browser` resolve to ONE zone for
 * the whole session, but the service zone belongs to the record being rendered
 * — two visits on the same screen can legitimately resolve differently. That is
 * why callers resolve per record through `zoneFor()` / `format(..., siteTz)`
 * rather than reading `displayTimezone` directly.
 */
export type ScheduleDisplayTimezoneMode = 'tenant' | 'browser' | 'service';

const DEFAULT_MODE: ScheduleDisplayTimezoneMode = 'tenant';

export function canonicalTimeZone(zone: string | null | undefined): string | undefined {
  const valid = safeTimeZone(zone);
  if (!valid) return undefined;
  return new Intl.DateTimeFormat('en-US', { timeZone: valid }).resolvedOptions().timeZone;
}

/** Treat IANA aliases that Intl canonicalizes to the same zone as equivalent. */
export function timeZonesMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const canonicalLeft = canonicalTimeZone(left);
  const canonicalRight = canonicalTimeZone(right);
  return Boolean(canonicalLeft && canonicalRight && canonicalLeft === canonicalRight);
}

/**
 * What to call each mode in UI chrome ("PDT · service time").
 *
 * Exhaustive over the union on purpose: the two-way
 * `mode === 'tenant' ? 'company time' : 'my time'` this replaces silently
 * labelled `service` mode as "my time", contradicting the toggle the operator
 * had just set. A fourth mode would now fail the build here instead.
 */
export const SCHEDULE_MODE_LABELS: Record<ScheduleDisplayTimezoneMode, string> = {
  tenant: 'company time',
  browser: 'my time',
  service: 'service time',
};

export function resolveScheduleDisplayZone(
  mode: ScheduleDisplayTimezoneMode,
  tenantTz: string | null | undefined,
  browserTz: string = getViewerTimeZone(),
  siteTz?: string | null,
): string {
  const tenant = safeTimeZone(tenantTz);
  if (mode === 'service') {
    // A record with no geocoded site zone (legacy rows, an ungeocodable
    // address) falls back to the company zone rather than the browser: the
    // point of this mode is to read the customer's clock, and the company zone
    // is the closest available stand-in. Browser would be arbitrary.
    return safeTimeZone(siteTz) ?? tenant ?? browserTz;
  }
  return mode === 'tenant' && tenant ? tenant : browserTz;
}

/**
 * The location zone of a record: its geocoded service-site zone, else its
 * company stamp.
 *
 * The bare `record.siteTimezone ?? record.timezone` this replaces looks
 * equivalent and is not. `??` only rejects null/undefined, so a MALFORMED site
 * zone (a bad import, a hand-edited row) passed straight through to
 * `formatDateTimeInTimeZone`, whose `safeTimeZone` guard then dropped the
 * timeZone option entirely and rendered in the VIEWER's zone — the one zone
 * this value must never resolve to. Validating first keeps the documented
 * fallback: site, else company.
 */
export function locationZoneOf(
  siteTimezone: string | null | undefined,
  fallbackTimezone: string | null | undefined,
): string | undefined {
  return safeTimeZone(siteTimezone) ?? safeTimeZone(fallbackTimezone) ?? undefined;
}

export function formatInScheduleTz(
  instant: string | null | undefined,
  zone: string,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' },
  browserTz: string = getViewerTimeZone(),
  /**
   * Append the zone's abbreviation when it differs from the viewer's.
   *
   * Off for a LIST that already names its clock once above it — the slot
   * picker heads its grid with "Company time (CDT)", and repeating CDT on both
   * ends of every time under it says the same thing twenty times. On a lone
   * time with no such header the abbreviation is the only thing stopping an
   * operator in another zone from reading it as their own clock, so it stays
   * on by default.
   */
  withAbbrev: boolean = true,
): string {
  if (!instant) return '';
  const formatted = formatDateTimeInTimeZone(instant, zone, options);
  if (!formatted) return '';
  if (!withAbbrev) return formatted;
  if (timeZonesMatch(zone, browserTz)) return formatted;
  const at = new Date(instant);
  const abbreviation = timeZoneAbbrev(zone, Number.isNaN(at.getTime()) ? undefined : at);
  return abbreviation ? `${formatted} ${abbreviation}` : formatted;
}

export interface ScheduleTimezone {
  mode: ScheduleDisplayTimezoneMode;
  setMode: (mode: ScheduleDisplayTimezoneMode) => void;
  tenantTimezone: string | undefined;
  browserTimeZone: string;
  /**
   * The session-wide zone: what a schedule instant renders in when the caller
   * has no service zone to offer. In `service` mode this is the FALLBACK, not
   * the answer — prefer `zoneFor(record.siteTimezone)`.
   */
  displayTimezone: string;
  hasDisplayChoice: boolean;
  /** Resolve the display zone for ONE record, given its service-site zone. */
  zoneFor: (siteTimezone?: string | null) => string;
  differsFromDisplay: (zone?: string | null, siteTimezone?: string | null) => boolean;
  format: (
    instant: string | null | undefined,
    options?: Intl.DateTimeFormatOptions,
    siteTimezone?: string | null,
  ) => string;
  /** `format`, without the zone abbreviation — for lists that head their own. */
  formatPlain: (
    instant: string | null | undefined,
    options?: Intl.DateTimeFormatOptions,
    siteTimezone?: string | null,
  ) => string;
}

type ScheduleTimezoneModeState = [
  ScheduleDisplayTimezoneMode,
  (mode: ScheduleDisplayTimezoneMode) => void,
];

const ScheduleTimezoneContext = createContext<ScheduleTimezoneModeState | null>(null);

/** Keep a record's display choice local while inheriting the viewer's saved default. */
export function ScheduleTimezoneScope({ children }: { children: ReactNode }) {
  const [inheritedMode] = useScheduleTimezoneMode();
  const [localMode, setLocalMode] = useState<ScheduleDisplayTimezoneMode | null>(null);
  const value = useMemo<ScheduleTimezoneModeState>(
    () => [localMode ?? inheritedMode, setLocalMode],
    [localMode, inheritedMode],
  );
  return createElement(ScheduleTimezoneContext.Provider, { value }, children);
}

export function useScheduleTimezoneMode(): [
  ScheduleDisplayTimezoneMode,
  (mode: ScheduleDisplayTimezoneMode) => void,
] {
  const scoped = useContext(ScheduleTimezoneContext);
  const profile = useMyProfile();
  const update = useUpdateMyScheduleDisplayTimezone();
  const stored = profile.data?.scheduleDisplayTimezoneMode;
  const mode: ScheduleDisplayTimezoneMode =
    stored === 'BROWSER' ? 'browser' : stored === 'SERVICE' ? 'service' : DEFAULT_MODE;
  const setMode = useCallback(
    (next: ScheduleDisplayTimezoneMode) => {
      if (next === mode || update.isPending) return;
      update.mutate({
        mode: next === 'browser' ? 'BROWSER' : next === 'service' ? 'SERVICE' : 'TENANT',
      });
    },
    [mode, update],
  );
  return scoped ?? [mode, setMode];
}

export function useScheduleTimezone(): ScheduleTimezone {
  const [mode, setMode] = useScheduleTimezoneMode();
  const tenantTimezone = useCompanyTimezone();
  const browserTimeZone = getViewerTimeZone();
  const displayTimezone = resolveScheduleDisplayZone(mode, tenantTimezone, browserTimeZone);
  // A third mode changed what "has a choice" means. This used to be a pure
  // tenant-vs-browser test, so it went false whenever those two matched — which
  // with `service` added would hide the toggle from every operator whose
  // browser already matches the company zone, making Service time unreachable
  // for exactly the single-zone tenant most likely to have out-of-zone JOBS.
  // Worse, an operator with SERVICE already saved would lose the control and be
  // stranded in a mode they could not leave. `service` resolves per RECORD, so
  // it stays a distinct outcome even when the session zones are identical.
  const hasDisplayChoice = Boolean(canonicalTimeZone(tenantTimezone)) || mode !== DEFAULT_MODE;
  const zoneFor = useCallback(
    (siteTimezone?: string | null) =>
      resolveScheduleDisplayZone(mode, tenantTimezone, browserTimeZone, siteTimezone),
    [mode, tenantTimezone, browserTimeZone],
  );
  // `zone` is the zone being TESTED (usually a record's site zone, for an "at
  // location" hint); `siteTimezone` resolves what this record renders in. In
  // service mode those collapse to the same value, so the hint correctly
  // disappears — the primary time already IS the location's time.
  const differsFromDisplay = useCallback(
    (zone?: string | null, siteTimezone?: string | null) =>
      Boolean(zone && !timeZonesMatch(zone, zoneFor(siteTimezone))),
    [zoneFor],
  );

  const format = useCallback(
    (
      instant: string | null | undefined,
      options?: Intl.DateTimeFormatOptions,
      siteTimezone?: string | null,
    ) => formatInScheduleTz(instant, zoneFor(siteTimezone), options, browserTimeZone),
    [zoneFor, browserTimeZone],
  );

  const formatPlain = useCallback(
    (
      instant: string | null | undefined,
      options?: Intl.DateTimeFormatOptions,
      siteTimezone?: string | null,
    ) => formatInScheduleTz(instant, zoneFor(siteTimezone), options, browserTimeZone, false),
    [zoneFor, browserTimeZone],
  );

  return useMemo(
    () => ({
      mode,
      setMode,
      tenantTimezone,
      browserTimeZone,
      displayTimezone,
      hasDisplayChoice,
      zoneFor,
      differsFromDisplay,
      format,
      formatPlain,
    }),
    [
      mode,
      setMode,
      tenantTimezone,
      browserTimeZone,
      displayTimezone,
      hasDisplayChoice,
      zoneFor,
      differsFromDisplay,
      format,
      formatPlain,
    ],
  );
}
